import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { MultiSelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { RedoAdminClientContext } from "../../../client/context";
import { getSystemRules, getSystemViews } from "../../../client/team";
import { TeamContext } from "../../team";
import { setupRules, setupViews } from "./support-onboarding-actions";

export const SupportOnboarding = memo(function SupportOnboarding() {
  const team = useContext(TeamContext);
  const client = useRequiredContext(RedoAdminClientContext);

  const systemRules = useLoad(async () => {
    const result = await getSystemRules(client);
    return result.data;
  }, [client]);

  const systemViews = useLoad(async () => {
    const result = await getSystemViews(client);
    return result;
  }, [client]);

  const [rulesToCreate, setRulesToCreate] = useState<readonly string[]>([]);
  const [viewsToCreate, setViewsToCreate] = useState<readonly string[]>([]);

  async function handleSetupRules() {
    if (!team) {
      return;
    }
    const ruleIds = new Set(rulesToCreate);
    const rules = (systemRules.value || []).filter((rule) =>
      ruleIds.has(rule._id),
    );
    try {
      await setupRules({ client, teamId: team._id, rules });
      toast("Rules created successfully", { variant: "success" });
    } catch (e) {
      toast("Error creating rules", { variant: "error" });
    }
  }

  async function handleSetupViews() {
    if (!team) {
      return;
    }
    const viewIds = new Set(viewsToCreate);
    const views = (systemViews.value || []).filter((view) =>
      viewIds.has(view._id),
    );
    try {
      await setupViews({ client, teamId: team._id, views });
      toast("Views created successfully", { variant: "success" });
    } catch (e) {
      toast("Error creating views", { variant: "error" });
    }
  }

  return (
    <Card borderRadius="md" title="Support onboarding">
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" fontWeight="medium">
          Rules and views are pulled from the team "[Data] Redo Default Email
          Templates"
        </Text>

        <Flex dir="row" gap="xl" wrap="wrap">
          <Flex dir="column" gap="md" style={{ minWidth: 250, flex: 1 }}>
            <Text fontSize="md" fontWeight="semibold">
              Rules
            </Text>
            <MultiSelectDropdown
              options={(systemRules.value || []).map((rule) => rule._id)}
              value={rulesToCreate}
              valueChange={(value) => setRulesToCreate(value || [])}
            >
              {(option) => {
                return (
                  <Text fontSize="sm">
                    {
                      (systemRules.value || []).find(
                        (rule) => rule._id === option,
                      )?.name
                    }
                  </Text>
                );
              }}
            </MultiSelectDropdown>
            <RedoButton
              disabled={rulesToCreate.length === 0}
              hierarchy="primary"
              onClick={handleSetupRules}
              size="sm"
              text="Create these rules for this team"
            />
          </Flex>

          <Flex dir="column" gap="md" style={{ minWidth: 250, flex: 1 }}>
            <Text fontSize="md" fontWeight="semibold">
              Views
            </Text>
            <MultiSelectDropdown
              options={(systemViews.value || []).map((view) => view._id)}
              value={viewsToCreate}
              valueChange={(value) => setViewsToCreate(value || [])}
            >
              {(option) => {
                return (
                  <Text fontSize="sm">
                    {
                      (systemViews.value || []).find(
                        (view) => view._id === option,
                      )?.name
                    }
                  </Text>
                );
              }}
            </MultiSelectDropdown>
            <RedoButton
              disabled={viewsToCreate.length === 0}
              hierarchy="primary"
              onClick={handleSetupViews}
              size="sm"
              text="Create these views for this team"
            />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
});
